import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { MdOutlineInbox, MdDeleteOutline } from 'react-icons/md';
import { ReactComponent as PdfFileIcon } from '../../res/assets/document_pdf.svg';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ConfirmDialog from '../../components/ConfirmDialog';
import LoadingWheel from '../../components/LoadingWheel';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import TitleBar from '../../components/TitleBar';
import buttonTheme from '../../res/styles/theme_button.module.scss';
import colors from '../../res/styles/Colors';
import ButtonRadio from '../TechFormList/components/ButtonRadio';
import { createTag, deleteTag, getTags, saveResource } from '../../service/ResourceService';
import { toastError, toastSuccess } from '../../ui/ToastHelper';
import ResourceTagSelect from './components/ResourceTagSelect';

const TYPE_VALUES = [{ name: 'PDF' }, { name: 'URL' }];
const TITLE_MAX_LENGTH = 255;
const URL_MAX_LENGTH = 255;
const PDF_MAX_SIZE = 10 * 1024 * 1024;
const ILLEGAL_TITLE_RE = /[/\\:*?"<>|]/;
const ILLEGAL_CHARS_DISPLAY = '/ \\ : * ? " < > |';

export default function ResourceForm() {
    const history = useHistory();
    const queryClient = useQueryClient();
    const location = useLocation();
    const { id } = useParams();
    const isEdit = id != null;
    const resource = location.state?.resource ?? null;

    const [title, setTitle] = useState('');
    const [titleError, setTitleError] = useState('');
    const [type, setType] = useState('pdf');
    const [selectedTags, setSelectedTags] = useState([]);
    const [urlValue, setUrlValue] = useState('');
    const [file, setFile] = useState(null);
    const [fileDeleted, setFileDeleted] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const fileInputRef = useRef(null);

    const { data: allTags = [] } = useQuery({
        queryKey: ['resourceTags'],
        queryFn: getTags,
        staleTime: Infinity,
        gcTime: Infinity
    });

    useEffect(() => {
        if (isEdit && resource == null) {
            history.replace('/resources');
            return;
        }
        setTitle(resource?.title ?? '');
        setTitleError('');
        setType(resource?.type ?? 'pdf');
        setSelectedTags(resource?.tags ? [...resource.tags] : []);
        setUrlValue(resource?.url ?? '');
        setFile(null);
        setFileDeleted(false);
    }, []);

    const validateTitleChars = (value) => {
        if (ILLEGAL_TITLE_RE.test(value)) {
            setTitleError(`The title contains invalid characters: ${ILLEGAL_CHARS_DISPLAY}`);
            return false;
        }
        setTitleError('');
        return true;
    };

    const handleTitleChange = (e) => {
        const val = e.target.value.slice(0, TITLE_MAX_LENGTH);
        setTitle(val);
        if (titleError) validateTitleChars(val);
    };

    const handleTitleBlur = () => {
        validateTitleChars(title);
    };

    const handleTypeChange = (value) => {
        if (value) setType(value.toLowerCase());
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer.files[0];
        if (dropped) setFile(dropped);
    };

    const handleFileInput = (e) => {
        const selected = e.target.files[0];
        if (selected) setFile(selected);
    };

    const handleSave = async () => {
        if (!title.trim()) {
            toastError('Title is required.');
            return;
        }
        if (!validateTitleChars(title)) {
            toastError(`The title contains invalid characters: ${ILLEGAL_CHARS_DISPLAY}`);
            return;
        }
        if (type === 'url') {
            if (!urlValue.trim()) {
                toastError('URL is required.');
                return;
            }
            if (urlValue.length > URL_MAX_LENGTH) {
                toastError(`The link is too long (max ${URL_MAX_LENGTH} characters).`);
                return;
            }
        }
        if (type === 'pdf') {
            const hasExistingFile = isEdit && !fileDeleted;
            if (!file && !hasExistingFile) {
                toastError('Please select a PDF file.');
                return;
            }
            if (file) {
                if (file.type !== 'application/pdf') {
                    toastError('The file must be in PDF format.');
                    return;
                }
                if (file.size > PDF_MAX_SIZE) {
                    toastError('The PDF file cannot exceed 10 MB.');
                    return;
                }
            }
        }

        setIsSaving(true);
        try {
            const resolvedTags = await Promise.all(
                selectedTags.map((tag) =>
                    tag.id == null ? createTag(tag.name) : Promise.resolve(tag)
                )
            );

            await saveResource({
                id: resource?.id,
                title: title.trim(),
                type,
                tagIds: resolvedTags.map((t) => t.id),
                url: urlValue.trim(),
                file
            });

            toastSuccess(isEdit ? 'Resource updated.' : 'Resource added.');
            queryClient.invalidateQueries({ queryKey: ['resources'] });
            queryClient.invalidateQueries({ queryKey: ['resourceTags'] });
            history.push('/resources');
        } catch {
            toastError('An error occurred while saving the resource.');
            setIsSaving(false);
        }
    };

    const isDirty =
        title !== (resource?.title ?? '') ||
        urlValue !== (resource?.url ?? '') ||
        file !== null ||
        fileDeleted ||
        selectedTags.length !== (resource?.tags?.length ?? 0) ||
        selectedTags.some((t, i) => t.name !== (resource?.tags?.[i]?.name ?? ''));

    const handleBackClick = () => {
        if (isDirty) setShowDiscardModal(true);
        else history.goBack();
    };

    const handleDiscardConfirm = (_, confirmed) => {
        setShowDiscardModal(false);
        if (confirmed) history.goBack();
    };

    const handleDeleteTag = async (tag) => {
        try {
            await deleteTag(tag.id);
            setSelectedTags((prev) => prev.filter((t) => t.id !== tag.id));
            queryClient.invalidateQueries({ queryKey: ['resourceTags'] });
        } catch {
            toastError('An error occurred while deleting the tag.');
        }
    };

    const typeDefaultIndex = (resource?.type ?? 'pdf') === 'pdf' ? 0 : 1;

    return (
        <PageWrapper>
            <NavigationBar />
            <TitleBar title={isEdit ? 'Edit Resource' : 'Add Resource'} hasBack onBack={handleBackClick} />
            <FormContent>
                <FormGroup>
                    <Label>Type:</Label>
                    <TypeWrapper $disabled={isEdit}>
                        <ButtonRadio
                            key={resource?.id ?? 'new'}
                            values={TYPE_VALUES}
                            defaultValueIndex={typeDefaultIndex}
                            onChangeValue={handleTypeChange}
                            className={buttonTheme.lighterGreen}
                            onActiveStyle={buttonTheme.green}
                        />
                    </TypeWrapper>
                </FormGroup>

                <FormGroup>
                    <Label>Title:</Label>
                    <StyledInput
                        type="text"
                        value={title}
                        onChange={handleTitleChange}
                        onBlur={handleTitleBlur}
                        placeholder="Enter a clear and descriptive title"
                        maxLength={TITLE_MAX_LENGTH}
                        disabled={isSaving}
                        $hasError={!!titleError}
                    />
                    {titleError && <ErrorHint>{titleError}</ErrorHint>}
                </FormGroup>

                <FormGroup>
                    <Label>Tag</Label>
                    <ResourceTagSelect
                        allTags={allTags}
                        selectedTags={selectedTags}
                        onChange={setSelectedTags}
                        onDeleteTag={handleDeleteTag}
                    />
                </FormGroup>

                {type === 'url' && (
                    <FormGroup>
                        <Label>URL:</Label>
                        <StyledInput
                            type="text"
                            value={urlValue}
                            onChange={(e) => setUrlValue(e.target.value)}
                            placeholder="Paste here the link"
                            disabled={isSaving}
                        />
                    </FormGroup>
                )}

                {type === 'pdf' && (
                    <FormGroup>
                        <Label>File:</Label>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf"
                            style={{ display: 'none' }}
                            onChange={handleFileInput}
                        />

                        {/* File already selected (new upload) */}
                        {file && (
                            <FileRow>
                                <FileRowInner>
                                    <PdfFileIcon width={19} height={24} />
                                    <FileName>{file.name}</FileName>
                                    {!isSaving && (
                                        <TrashButton
                                            type="button"
                                            onClick={() => {
                                                setFile(null);
                                                if (fileInputRef.current) fileInputRef.current.value = '';
                                            }}
                                        >
                                            <MdDeleteOutline size={20} />
                                        </TrashButton>
                                    )}
                                </FileRowInner>
                            </FileRow>
                        )}

                        {/* Existing saved file (edit mode, not deleted, no new file picked) */}
                        {!file && isEdit && !fileDeleted && (
                            <FileRow>
                                <FileRowInner>
                                    <PdfFileIcon width={19} height={24} />
                                    <FileName>{resource.title}.pdf</FileName>
                                    <TrashButton
                                        type="button"
                                        onClick={() => setFileDeleted(true)}
                                        disabled={isSaving}
                                    >
                                        <MdDeleteOutline size={20} />
                                    </TrashButton>
                                </FileRowInner>
                            </FileRow>
                        )}

                        {/* Dropzone: add mode OR edit with file deleted */}
                        {!file && (!isEdit || fileDeleted) && (
                            <DropZone
                                $isDragging={isDragging}
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <MdOutlineInbox size={48} color={colors.green} />
                                <DropZoneText>Click or drag a file to this area to upload.</DropZoneText>
                                <DropZoneHint>PDF format. Up to 10 MB.</DropZoneHint>
                                <UploadButton
                                    type="button"
                                    className={buttonTheme.green}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        fileInputRef.current?.click();
                                    }}
                                    disabled={isSaving}
                                >
                                    Upload file
                                </UploadButton>
                            </DropZone>
                        )}
                    </FormGroup>
                )}

                <Footer>
                    <AddButton
                        type="button"
                        className={buttonTheme.green}
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isEdit ? 'SAVE CHANGES' : 'ADD RESOURCE'}
                    </AddButton>
                </Footer>
            </FormContent>
            {isSaving && (
                <SaveOverlay>
                    <LoadingWheel isVisible centerMode='absolute' width={80} height={80} />
                </SaveOverlay>
            )}
            <ConfirmDialog
                isOpen={showDiscardModal}
                text={"Discard changes?\nYou have unsaved changes that will be lost."}
                elementId="discard"
                cancelClassName={buttonTheme.gray}
                confirmClassName={buttonTheme.red}
                confirmText="DISCARD"
                onConfirm={handleDiscardConfirm}
            />
        </PageWrapper>
    );
}

const PageWrapper = styled.div`
    min-height: 100vh;
    background-color: #f5f5f5;
`;

const FormContent = styled.div`
    width: 70%;
    margin: 24px auto 0;

    @media (max-width: 575px) {
        width: 92%;
    }
`;

const FormGroup = styled.div`
    margin-bottom: 20px;
`;

const Label = styled.label`
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.darkest_gray};
    margin-bottom: 8px;
`;

const StyledInput = styled.input`
    width: 100%;
    height: 40px;
    border-radius: 2px;
    border: 1px solid ${({ $hasError }) => ($hasError ? colors.red : '#d9d9d9')};
    padding: 0 12px;
    font-size: 14px;
    background: white;

    &:focus {
        outline: none;
        border-color: ${({ $hasError }) => ($hasError ? colors.red : colors.green)};
        box-shadow: 0 0 0 2px ${({ $hasError }) =>
            $hasError ? 'rgba(239,64,62,0.15)' : 'rgba(38,181,102,0.15)'};
    }

    &::placeholder {
        color: #bfbfbf;
    }

    &:disabled {
        background-color: #f5f5f5;
        cursor: not-allowed;
    }
`;

const ErrorHint = styled.span`
    display: block;
    margin-top: 4px;
    font-size: 12px;
    color: ${colors.red};
`;

const TypeWrapper = styled.div`
    width: 180px;
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
    opacity: ${({ $disabled }) => ($disabled ? 0.65 : 1)};
`;

const DropZone = styled.div`
    border: 1px solid ${({ $isDragging }) => ($isDragging ? colors.green : '#d9d9d9')};
    border-radius: 2px;
    background-color: ${({ $isDragging }) => ($isDragging ? '#f0faf5' : 'white')};
    padding: 32px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: border-color 0.2s, background-color 0.2s;

    &:hover {
        border-color: ${colors.green};
        background-color: #f9fffe;
    }
`;

const DropZoneText = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${colors.darkest_gray};
    text-align: center;
`;

const DropZoneHint = styled.p`
    margin: 0;
    font-size: 12px;
    color: ${colors.dark_gray};
`;

const UploadButton = styled.button`
    margin-top: 8px;
    padding: 6px 20px;
    border-radius: 26px;
    font-size: 13px;
    border: none;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
    padding-bottom: 32px;

    @media (max-width: 575px) {
        justify-content: stretch;
    }
`;

const AddButton = styled.button`
    padding: 8px 24px;
    border-radius: 26px;
    font-size: 13px;
    border: none;
    cursor: pointer;
    letter-spacing: 0.5px;

    @media (max-width: 575px) {
        width: 100%;
        padding: 12px 24px;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const FileRow = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid #d9d9d9;
    border-radius: 2px;
    background: white;
    padding: 10px 14px;
`;

const FileRowInner = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const FileName = styled.span`
    flex: 1;
    font-size: 14px;
    color: ${colors.darkest_gray};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const SaveOverlay = styled.div`
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.35);
    z-index: 2000;
`;

const TrashButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    color: ${colors.darkest_gray};
    flex-shrink: 0;

    &:hover {
        color: ${colors.red};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

